import type { ButtonHTMLAttributes, CSSProperties, ElementType } from 'react';
import { forwardRef } from 'react';

import { baseClasses, variantClasses, type ButtonVariant } from './button-styles';

/**
 * Props for the IconButton component
 */
export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The icon to display */
  icon: React.ReactNode;
  /** Accessible label for the button */
  'aria-label': string;
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size variant */
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Element type to render as (default: 'button'). Use 'span' or 'div' when inside another button. */
  as?: ElementType;
}

// Padding-based sizes using CSS variables for flexibility
const sizeStyles: Record<string, CSSProperties> = {
  xxs: {
    padding: 'var(--uiv2-icon-btn-padding-xxs, 7px)',
    fontSize: 'var(--uiv2-btn-font-xxs, 12px)',
  },
  xs: {
    padding: 'var(--uiv2-icon-btn-padding-xs, 11px)',
    fontSize: 'var(--uiv2-btn-font-xs, 14px)',
  },
  sm: {
    padding: 'var(--uiv2-icon-btn-padding-sm, 10px)',
    fontSize: 'var(--uiv2-btn-font-sm, 14px)',
  },
  md: {
    padding: 'var(--uiv2-icon-btn-padding-md, 12px)',
    fontSize: 'var(--uiv2-btn-font-md, 16px)',
  },
  lg: {
    padding: 'var(--uiv2-icon-btn-padding-lg, 14px)',
    fontSize: 'var(--uiv2-btn-font-lg, 16px)',
  },
  xl: {
    padding: 'var(--uiv2-icon-btn-padding-xl, 16px)',
    fontSize: 'var(--uiv2-btn-font-xl, 16px)',
  },
  '2xl': {
    padding: 'var(--uiv2-icon-btn-padding-2xl, 20px)',
    fontSize: 'var(--uiv2-btn-font-2xl, 18px)',
  },
};

/**
 * IconButton component - a square button designed for icons
 * Shares the same variants and focus states as Button
 *
 * @example
 * ```tsx
 * <IconButton
 *   icon={<TrashIcon />}
 *   aria-label="Delete item"
 *   variant="ghost"
 *   size="sm"
 *   onClick={handleDelete}
 * />
 * ```
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      className = '',
      variant = 'outline',
      size = 'md',
      disabled,
      style,
      as: Component = 'button',
      ...props
    },
    ref
  ) => {
    return (
      <Component
        ref={ref}
        type={Component === 'button' ? 'button' : undefined}
        disabled={Component === 'button' ? disabled : undefined}
        role={Component !== 'button' ? 'button' : undefined}
        tabIndex={Component !== 'button' ? (disabled ? -1 : 0) : undefined}
        aria-disabled={Component !== 'button' ? disabled : undefined}
        className={`${baseClasses} ${variantClasses[variant]} ${disabled && Component !== 'button' ? 'disabled:bg-uiv2-btn-disabled-bg disabled:text-uiv2-btn-disabled-text cursor-not-allowed pointer-events-none' : ''} ${className}`}
        style={{ ...sizeStyles[size], ...style }}
        {...props}
      >
        {icon}
      </Component>
    );
  }
);

IconButton.displayName = 'IconButton';
